/**
 * platform/web/src/ui/state/saveorch.ts
 *
 * The save-data ORCHESTRATION seam that finishes the round-trip end to end.
 *
 * The pieces already exist and are tested in isolation:
 *   - the C core serialises/deserialises its writable save VFS to an opaque blob;
 *   - the FFI exposes export/import; the CPU Runner imports a blob before a run
 *     and posts a `saveData` (the blob) when the run ends;
 *   - {@link SaveDataStorage} / {@link persistSave} durably store a blob keyed by
 *     a title id, and the manifest accounts for it.
 *
 * What was MISSING is the glue on the main thread that ties a live run to that
 * storage. Two impedance mismatches make this non-trivial, and both are handled
 * here as PURE, unit-testable logic so the wiring in main.ts / App.tsx stays
 * trivial:
 *
 *   1. KEYING. The worker protocol keys a run by the file NAME (`loadGame.name`
 *      and `saveData.name`), but durable storage + the library + the manifest are
 *      keyed by the content-derived {@link Title} id (`format:size:name`). A run
 *      therefore has to be REGISTERED (name -> titleId) at load time so the
 *      `saveData` message that arrives at the end can be mapped back to the title
 *      it belongs to. {@link SaveOrchestrator} owns that small registry.
 *
 *   2. ORDERING. The save to import must be read from storage BEFORE `loadGame`
 *      is posted; the save to persist arrives asynchronously AFTER the run via the
 *      `saveData` message. {@link resolveImport} (read-before-load) and
 *      {@link resolveSaveTitleId} (map a `saveData.name` back to its titleId) are
 *      the two pure resolutions the wiring composes with the already-tested
 *      {@link persistSave}.
 *
 * §1.6: every blob here is the GUEST's own runtime save data — an opaque buffer
 * we move and account for, never a ROM, a key, or decrypted content. Nothing in
 * this module reads or interprets the bytes.
 */

import { titleId as deriveTitleId, removeTitle } from "./library";
import type { LibraryState } from "./library";
import { removeSave } from "./savedata";
import type { SaveDataManifest, SaveDataStorage } from "./savedata";
import type { GameFormat } from "../../formats";

/** The fields a run is identified by at load time, enough to derive a titleId. */
export interface RunIdentity {
  readonly name: string;
  readonly size: number;
  readonly format: GameFormat;
}

/**
 * Tracks the name -> titleId mapping for in-flight / recent runs so a `saveData`
 * message (keyed by name) can be persisted under the right title id. Tiny and
 * deterministic; no I/O. The wiring registers a run when it posts `loadGame` and
 * resolves the title id when a `saveData` arrives.
 *
 * A new run with the SAME name (e.g. re-running a homebrew after editing it so
 * its size/format changed) overwrites the prior mapping — last run wins, which is
 * exactly the run whose save is about to be flushed.
 */
export class SaveOrchestrator {
  private readonly nameToTitleId = new Map<string, string>();

  /**
   * Register a run: derive its titleId from the identity and remember the
   * name -> titleId mapping. Returns the derived titleId so the caller can read
   * the persisted save to import. Idempotent for the same identity.
   */
  register(identity: RunIdentity): string {
    const id = deriveTitleId(identity);
    this.nameToTitleId.set(identity.name, id);
    return id;
  }

  /**
   * Resolve the titleId for a `saveData` message's name, or null if no run with
   * that name was registered (e.g. a save arrived for a run we never tracked —
   * we drop it rather than persist under a guessed key).
   */
  resolveSaveTitleId(name: string): string | null {
    return this.nameToTitleId.get(name) ?? null;
  }

  /** Forget a run's mapping (e.g. once its save has been persisted). Optional —
   *  leaving stale entries is harmless (last-run-wins on re-register). */
  forget(name: string): void {
    this.nameToTitleId.delete(name);
  }
}

/**
 * Read the persisted save blob to import for a title, ready to attach to the
 * `loadGame.save` slot. Returns the bytes (as an ArrayBuffer, tightly sized so
 * the transfer carries no slack) or null when there's no stored save. Degrades
 * to null on any storage error — a missing/unreadable save just means the guest
 * starts fresh, never a failed load. §1.6: opaque bytes, never interpreted.
 */
export async function resolveImport(
  storage: { read(titleId: string): Promise<Uint8Array | null> },
  titleId: string,
): Promise<ArrayBuffer | null> {
  let bytes: Uint8Array | null;
  try {
    bytes = await storage.read(titleId);
  } catch {
    return null;
  }
  if (bytes === null || bytes.byteLength === 0) return null;
  // Copy into a freshly-sized ArrayBuffer: the stored Uint8Array may be a view
  // over a larger backing buffer, and the result is handed to a transfer list.
  const copy = bytes.slice();
  return copy.buffer.byteLength === copy.byteLength
    ? copy.buffer
    : copy.buffer.slice(copy.byteOffset, copy.byteOffset + copy.byteLength);
}

/**
 * The shape of an outgoing `loadGame` worker message paired with its transfer
 * list. Mirrors the protocol's `loadGame` variant structurally (so main.ts can
 * use it as-is) without coupling this module — and its Node test — to the full
 * worker protocol module.
 */
export interface PreparedLoadGame {
  readonly message: {
    readonly type: "loadGame";
    readonly data: ArrayBuffer;
    readonly name: string;
    readonly save?: ArrayBuffer;
  };
  /** The Transferable list for postMessage: always the image, plus the save if
   *  present, so both move zero-copy to the worker. */
  readonly transfer: ArrayBuffer[];
}

/**
 * Build the outgoing `loadGame` message + its transfer list, attaching a resolved
 * save blob only when there is one. This is the message-shaping half of the load
 * interception: main.ts derives the identity, registers the run, and reads the
 * save (the async {@link resolveImport}); this pure step turns the result into the
 * exact message/transfer pair to post. Extracting it makes the keying decision —
 * "attach save iff non-null, and transfer it alongside the image" — unit-testable
 * without a Worker. §1.6: `save` is the guest's own opaque save bytes, never
 * interpreted here.
 */
export function prepareLoadGame(
  data: ArrayBuffer,
  name: string,
  save: ArrayBuffer | null,
): PreparedLoadGame {
  if (save) {
    return {
      message: { type: "loadGame", data, name, save },
      transfer: [data, save],
    };
  }
  return {
    message: { type: "loadGame", data, name },
    transfer: [data],
  };
}

/**
 * A save blob flushed by a finished run, mapped to the title it belongs to and
 * ready to persist. The `bytes` are copied out of the (transferred) message
 * buffer so the result owns its memory.
 */
export interface ResolvedSave {
  readonly titleId: string;
  readonly bytes: Uint8Array;
}

/**
 * Resolve a `saveData` message (keyed by file NAME) to the title it should be
 * persisted under, or null to DROP it. A save for a run the orchestrator never
 * tracked (no name -> titleId mapping) is dropped rather than persisted under a
 * guessed key — exactly the decision the main-thread `saveData` handler must
 * make. On success the message bytes are copied into a fresh, owned
 * {@link Uint8Array} (the source is a transferred buffer). Extracting this makes
 * the drop-vs-persist decision unit-testable without a Worker. §1.6: opaque save
 * bytes — the guest's own runtime data, never interpreted.
 */
export function resolveSaveForPersist(
  orch: SaveOrchestrator,
  name: string,
  bytes: ArrayBuffer,
): ResolvedSave | null {
  const titleId = orch.resolveSaveTitleId(name);
  if (titleId === null) return null;
  return { titleId, bytes: new Uint8Array(bytes.slice(0)) };
}

/** The result of removing a title from the library: the pruned library state
 *  AND the manifest after the title's save was forgotten. The caller commits
 *  both to its signals + the persistence seams. */
export interface RemovedFromLibrary {
  readonly library: LibraryState;
  readonly manifest: SaveDataManifest;
}

/**
 * Remove a title from the library END TO END: drop it from the library model
 * AND delete its persisted save (blob + manifest accounting), returning the
 * pruned library state and the fresh manifest. This is the "remove from library"
 * orchestration the Detail screen invokes — the library forgets the title and we
 * don't leave its save data orphaned on the device behind a now-unreachable
 * entry.
 *
 * Pure orchestration over the tested {@link removeTitle} (library) +
 * {@link removeSave} (storage + manifest); never mutates the inputs. Removing a
 * title that isn't present, or one with no save, is harmless (each underlying
 * step is a no-op). §1.6: the save blob is the guest's OWN runtime data — an
 * opaque buffer we delete, never decrypted content; the library never held bytes.
 */
export async function removeFromLibrary(
  storage: SaveDataStorage,
  library: LibraryState,
  manifest: SaveDataManifest,
  titleId: string,
): Promise<RemovedFromLibrary> {
  const nextManifest = await removeSave(storage, manifest, titleId);
  return { library: removeTitle(library, titleId), manifest: nextManifest };
}
